import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  CircularProgress,
  Chip,
  Divider,
  Fade,
  Skeleton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CategoryIcon from "@mui/icons-material/Category";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WorkIcon from "@mui/icons-material/Work";
  import PersonIcon from "@mui/icons-material/Person";
import { useNavigate, useParams } from "react-router-dom";
import { convertTimeFormat } from "../../utils/timeFormatter";
import { getApplicationsCount } from "../../utils/applicationsTypesCounter";
import OrdersFilter from "./OrdersFilter";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import { useTranslation } from "react-i18next";

const MobileMyOrders = ({ appicationsData, isLoading }) => {
  const { status } = useParams();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [loadIndex, setLoadIndex] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const navigate = useNavigate();
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const hideCancelledRequests = configLinks?.hideCancelledRequests ?? false;
  const { t } = useTranslation();
  
  // Filter out cancelled orders if setting is enabled
  const baseData = hideCancelledRequests 
    ? appicationsData.filter(item => item.status?.id !== 6)
    : appicationsData;

  const statusMap = {
    1: { name: t('status_new'), color: "#fff", borderColor: "#1976d2", slug: "new" },
    2: { name: t('status_inProgress'), color: "#FFE18C", borderColor: "#f57c00", slug: "in-progress" },
    3: { name: t('status_pending'), color: "#65E0FF", borderColor: "#0288d1", slug: "pending" },
    4: { name: t('status_completed'), color: "#72EF9B", borderColor: "#388e3c", slug: "completed" },
    5: { name: t('status_rejected'), color: "#ff000072", borderColor: "#ef5555ff", slug: "rejected" },
    6: { name: t('status_cancelled'), color: "#D1D1D1", borderColor: "#757575", slug: "canceled" },
  };

  useEffect(() => {
    const statusId = Object.keys(statusMap).find(
      (key) => statusMap[key].slug === status
    );
    setSelectedStatus(statusId ? Number(statusId) : null);
  }, [status]);

  useEffect(() => {
    const filteredData =
      selectedStatus === null
        ? baseData
        : baseData.filter((item) => item.status.id === selectedStatus);
    
    // Apply additional filters if any
    let finalData = filteredData;
    if (appliedFilters) {
      finalData = filteredData.filter((item) => {
        let match = true;
        
        if (appliedFilters.fromDate) {
          match = match && new Date(item.insertDate) >= new Date(appliedFilters.fromDate);
        }
        if (appliedFilters.toDate) {
          match = match && new Date(item.insertDate) <= new Date(appliedFilters.toDate);
        }
        if (appliedFilters.serviceType) {
          match = match && item.serviceName === appliedFilters.serviceType;
        }
        
        return match;
      });
    }
    
    setDisplayedItems(finalData.slice(0, loadIndex));
  }, [selectedStatus, baseData, loadIndex, appliedFilters]);

  const handleFilterClick = (statusId) => {
    if (selectedStatus === statusId) {
      navigate(`/my-orders`);
    } else {
      navigate(`/my-orders/${statusMap[statusId].slug}`);
    }
    setLoadIndex(10); // Reset lazy load index on status change
  };

  const loadMore = () => {
    if (loadIndex < filteredData.length) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setLoadIndex((prev) => prev + 10);
        setIsLoadingMore(false);
      }, 300);
    }
  };

  const filteredData =
    selectedStatus === null
      ? baseData
      : baseData.filter((item) => item.status.id === selectedStatus);

  const handleViewClick = (item) => {
    navigate(`/order-details/${item.subscriberServiceId}`, {
      state: { ...location?.state, groupName: item.serviceName },
    });
  };

  const handleFilterApply = (filters) => {
    setAppliedFilters(filters);
    setLoadIndex(10);
    setSelectedStatus(null);
    navigate('/my-orders');
  };

  // Get data for counting based on applied filters
  let dataForCounting = baseData;
  if (appliedFilters) {
    dataForCounting = baseData.filter((item) => {
      let match = true;

      if (appliedFilters.fromDate) {
        match = match && new Date(item.insertDate) >= new Date(appliedFilters.fromDate);
      }
      if (appliedFilters.toDate) {
        match = match && new Date(item.insertDate) <= new Date(appliedFilters.toDate);
      }
      if (appliedFilters.serviceType) {
        match = match && item.serviceName === appliedFilters.serviceType;
      }

      return match;
    });
  }

  // Extract unique service types from data
  const uniqueServiceTypes = [...new Set(baseData.map(item => item.serviceName))];

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.scrollHeight - 100 &&
        !isLoadingMore
      ) {
        loadMore();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoadingMore, loadIndex, filteredData]);

  if (isLoading) {
    return (
      <Box sx={{ padding: 2 }}>
        {/* Skeleton for filters */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center",
            marginBottom: 3,
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((key) => (
            <Skeleton
              key={key}
              variant="rectangular"
              width={100}
              height={80}
              sx={{ borderRadius: "8px" }}
            />
          ))}
        </Box>

        {/* Skeleton for cards */}
        <Stack gap={2}>
          {[1, 2, 3].map((index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={200}
              sx={{ borderRadius: "8px" }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (!appicationsData || appicationsData.length === 0) {
    return (
      <Box sx={{ textAlign: "center", p: 5 }}>
        <ReceiptLongIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
          {t('noOrders')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('noOrdersDesc')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2, pb: 4 }}>
      {/* Filter Component */}
      <OrdersFilter onFilter={handleFilterApply} isMobile={true} serviceTypes={uniqueServiceTypes} />

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
          marginBottom: 3,
        }}
      >
        {Object.keys(statusMap).filter(key => !(hideCancelledRequests && Number(key) === 6)).map((key) => {
          const id = Number(key);
          const { name, color, borderColor } = statusMap[key];
          const applicationCount = getApplicationsCount(dataForCounting, id);

          return (
            <Paper
              key={id}
              onClick={() => handleFilterClick(id)}
              sx={{
                cursor: "pointer",
                padding: 2,
                minWidth: "90px",
                textAlign: "center",
                backgroundColor: color,
                borderRadius: "12px",
                border: selectedStatus === id ? `3px solid ${borderColor}` : "none",
                boxShadow: selectedStatus === id ? `0px 4px 12px ${borderColor}50` : 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0px 6px 16px ${borderColor}60`,
                },
                "&:active": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: borderColor }}
              >
                {applicationCount}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {name}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Empty State */}
      {displayedItems.length === 0 && !isLoading && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 2,
          }}
        >
          <ReceiptLongIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('noOrders')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {selectedStatus
              ? t('noOrders')
              : t('noOrdersDesc')}
          </Typography>
        </Box>
      )}

      {/* Cards */}
      <Stack gap={2.5}>
        {displayedItems.map((order, index) => (
          <Fade in key={index} timeout={300 + index * 50}>
            <Paper
              sx={{
                padding: 2.5,
                borderRadius: "12px",
                border: `2px solid ${statusMap[order.status.id]?.borderColor}`,
                backgroundColor: statusMap[order.status.id]?.color,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
              elevation={2}
            >
              <Stack gap={2}>
                {/* Status Chip */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Chip
                    label={statusMap[order.status.id]?.name}
                    size="small"
                    sx={{
                      backgroundColor: statusMap[order.status.id]?.borderColor,
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                  <IconButton
                    onClick={() => handleViewClick(order)}
                    sx={{
                      backgroundColor: statusMap[order.status.id]?.borderColor,
                      color: "white",
                      "&:hover": {
                        backgroundColor: statusMap[order.status.id]?.borderColor,
                        opacity: 0.8,
                      },
                    }}
                    size="small"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Divider />

                {/* Order Details */}
                <Box display="flex" gap={1.5} alignItems="center">
                  <ReceiptLongIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                    {t('col_orderNumber')}:
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    {order.subscriberServiceFullNumber}
                  </Typography>
                </Box>

                <Box display="flex" gap={1.5} alignItems="flex-start">
                  <CategoryIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20, mt: 0.3 }} />
                  <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                    {t('col_requestType')}:
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", flex: 1 }}>
                    {order.serviceName}
                  </Typography>
                </Box>

                <Box display="flex" gap={1.5} alignItems="center">
                  <AttachMoneyIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                    {t('col_fees')}:
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    {order.applicationFee} {order.currencyChar3Code}
                  </Typography>
                </Box>

                <Box display="flex" gap={1.5} alignItems="center">
                  <CalendarTodayIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                    {t('col_date')}:
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem", direction: "ltr", textAlign: "end" }}>
                    {convertTimeFormat(order.insertDate)}
                  </Typography>
                </Box>

                {order.activityName && (
                  <Box display="flex" gap={1.5} alignItems="center">
                    <WorkIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20 }} />
                    <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                      {t('col_activity')}:
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem" }}>
                      {order.activityName}
                    </Typography>
                  </Box>
                )}

                  {order.accountForName && (
                    <Box display="flex" gap={1.5} alignItems="center">
                      <PersonIcon sx={{ color: statusMap[order.status.id]?.borderColor, fontSize: 20 }} />
                      <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                        {t('col_accountFor')}:
                      </Typography>
                      <Typography sx={{ fontSize: "0.9rem" }}>
                        {order.accountForName}
                      </Typography>
                    </Box>
                  )}
              </Stack>
            </Paper>
          </Fade>
        ))}
      </Stack>

      {/* Loading More Indicator */}
      {isLoadingMore && (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* End of List Message */}
      {!isLoadingMore &&
        displayedItems.length > 0 &&
        displayedItems.length >= filteredData.length && (
          <Box sx={{ textAlign: "center", mt: 3, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('noMoreOrders')}
            </Typography>
          </Box>
        )}
    </Box>
  );
};

export default MobileMyOrders;


